import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * ErrorBoundary genérico para envolver secciones del panel admin.
 *
 * Uso: <ErrorBoundary label="Detalle de solicitud">…</ErrorBoundary>
 *
 * Si algún subcomponente lanza un error de render (por ejemplo por un
 * campo que ahora es objeto en vez de string), en vez de pantalla en
 * blanco mostramos un mensaje amigable con el error para poder
 * diagnosticar sin dejar al admin sin acceso al resto de la UI.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || 'unknown', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === 'function') this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      const msg = String(this.state.error?.message || this.state.error || 'Error inesperado');
      return (
        <div className="p-6 rounded-md bg-red-50 border border-red-200" data-testid="error-boundary-fallback">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-red-800">
                Ocurrió un error al mostrar {this.props.label || 'esta sección'}.
              </p>
              <p className="text-sm text-red-700 mt-1 break-words">{msg}</p>
              <button
                type="button"
                onClick={this.handleReset}
                className="mt-3 text-sm text-red-700 underline hover:no-underline"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
